import { useState, useEffect } from "react";
import { Box, Checkbox } from "@mui/material";
import TableComponent, { IColumnProps } from "../../../components/TableComponent";
import { IPaginationParams } from "../../../services/types";
import { t } from "i18next";
import { useDebounce } from "../../../services/SchoolTransportation/hooks/useDebounce";
import { useLocations } from "./LocationContext";
import { ILocationProps } from "../../../services/Location/types";

interface LocationListProps {
    onSelect: (id: string) => void;
    selectedIds: string[];
    setSelectedIds: (ids: string[]) => void;
    onClose?: () => void;
}

const LocationList = ({ onSelect, selectedIds, setSelectedIds }: LocationListProps) => {
    const [pagination, setPagination] = useState<IPaginationParams>({
        pageNumber: 1,
        pageSize: 10,
    });

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const locations = useLocations();


    useEffect(() => {
        if (locations.length > 0 && selectedIds.length === 0) {
            setSelectedIds(locations.map(l => l.entityId));
        }
    }, [locations]);

    const filteredLocations = locations.filter((loc) =>
        loc.entityId.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );

    const startIndex = (pagination.pageNumber - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const paginatedLocations = filteredLocations.slice(startIndex, endIndex);

    const columns: IColumnProps[] = [
        {
            id: "select",
            label: "",
            width: "40px",
            render: (_value: string, _rowIndex: number, row?: any) => {
                const loc = row as ILocationProps;
                return (
                    <Checkbox
                        size="small"
                        sx={{ p: 0, m: 0 }}
                        checked={selectedIds.includes(loc.entityId)}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setSelectedIds([...selectedIds, loc.entityId]);
                            } else {
                                setSelectedIds(selectedIds.filter(id => id !== loc.entityId));
                            }
                        }}
                    />
                );
            },
        },
        {
            id: "entityId",
            label: t("map.vehicle"),
            width: "20%",
            render: (value: string) => (
                <span
                    style={{
                        cursor: "pointer",
                        color: "white",
                        textDecoration: "underline",
                    }}
                    onClick={() => onSelect(value)}
                >
                    {value}
                </span>
            ),
        },
        {
            id: "latitude",
            label: t("map.latitude"),
            width: "20%",
            render: (value: string) => <span>{parseFloat(value).toFixed(4)}</span>,
        },
        {
            id: "longitude",
            label: t("map.longitude"),
            width: "20%",
            render: (value: string) => <span>{parseFloat(value).toFixed(4)}</span>,
        },
        {
            id: "timestamp",
            label: t("map.date"),
            width: "15%",
            render: (value: string) => <span>{new Date(value).toLocaleString()}</span>,
        },
    ];

    return (
        <TableComponent
            title={<span style={{ fontWeight: "bold" }}>{t("map.locations")}</span>}
            rows={paginatedLocations}
            columns={columns}
            isLoading={false}
            totalCount={filteredLocations.length}
            onChangePageOrRowsPerPage={setPagination}
            onChangeMainFilter={setSearchTerm}
        />
    );
};

export default LocationList;
